/**
 * usePageTracking Hook
 *
 * Automatically tracks page views when the route changes.
 * Use this hook in layout components or pages to track navigation.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackEvent, PageViewData, AnalyticsEvent } from '../services/pageViewService';

interface UsePageTrackingOptions {
  /** Enable automatic page view tracking on route change */
  autoTrack?: boolean;
  /** Custom page title (defaults to document.title) */
  pageTitle?: string;
  /** Additional event data to include */
  eventData?: Record<string, unknown>;
}

/**
 * Hook to track page views automatically on route changes
 */
export const usePageTracking = (options: UsePageTrackingOptions = {}) => {
  const { autoTrack = true, pageTitle, eventData } = options;
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    if (!autoTrack) return;

    // Avoid duplicate tracking on initial render
    const currentPath = location.pathname + location.hash;
    if (previousPath.current === currentPath) return;
    previousPath.current = currentPath;

    // Track the page view
    const pageViewData: PageViewData = {
      page_path: currentPath,
      page_title: pageTitle || document.title,
      page_referrer: document.referrer || undefined,
      event_data: eventData,
    };

    trackPageView(pageViewData);
  }, [location, autoTrack, pageTitle, eventData]);

  // Return manual tracking functions for custom events
  return {
    trackPageView: (data: Partial<PageViewData>) =>
      trackPageView({
        page_path: location.pathname + location.hash,
        ...data,
      }),
    trackEvent: (event: AnalyticsEvent) => trackEvent(event),
  };
};

/**
 * Hook for tracking specific user interactions
 */
export const useEventTracking = () => {
  const location = useLocation();

  const track = (eventType: string, eventData?: Record<string, unknown>) => {
    trackEvent({
      event_type: eventType,
      event_data: eventData,
      page_path: location.pathname + location.hash,
    });
  };

  return {
    trackClick: (elementName: string, metadata?: Record<string, unknown>) =>
      track('click', { element: elementName, ...metadata }),

    trackFormStart: (formName: string) =>
      track('form_start', { form_name: formName }),

    trackFormSubmit: (formName: string, success: boolean) =>
      track('form_submit', { form_name: formName, success }),

    trackScroll: (depth: number) =>
      track('scroll', { depth_percent: depth }),

    trackVideoPlay: (videoName: string) =>
      track('video_play', { video_name: videoName }),

    trackDownload: (fileName: string) =>
      track('download', { file_name: fileName }),

    trackOutboundLink: (url: string) =>
      track('outbound_link', { url }),

    trackCustom: track,
  };
};

export default usePageTracking;
